import { useEffect, useState } from "react";

// Chrome/Android fires "beforeinstallprompt" when the page qualifies as an
// installable PWA (manifest + service worker present, among other checks)
// and the browser hasn't already installed it. Chrome suppresses its own
// mini-infobar for pages that call preventDefault() on this event, so we
// stash it and offer our own "Install app" button instead — this hook is
// the only place that touches that event.
//
// iOS Safari and older Chrome never fire this event at all; callers should
// treat `available === false` as "no button", not as an error.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function usePwaInstall() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function promptInstall() {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    // Whatever the user chose, Chrome only allows the prompt to be used
    // once per captured event.
    setDeferredEvent(null);
  }

  return { available: !!deferredEvent && !installed, installed, promptInstall };
}
